import type { OnChangeFn } from "@dresscode-frontend/utility-types";
import { useCallback } from "react";

import IconButton from "@/components/Button/IconButton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { defaultNS, useTranslation } from "@/lib/i18n";

export type ReportStatusFilterValue = "all" | "completed" | "notCompleted";

type Props = {
  value?: ReportStatusFilterValue;
  onChange: OnChangeFn<ReportStatusFilterValue | undefined>;
};

const statusOptions: ReportStatusFilterValue[] = [
  "all",
  "completed",
  "notCompleted",
];

export function ReportStatusSelect({ value, onChange }: Props) {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "domain.device.components.deviceFilters.reportStatus",
  });

  const handleValueChange = useCallback(
    (newValue: string) => {
      if (newValue === "all") {
        onChange(undefined);
      } else {
        onChange(newValue as ReportStatusFilterValue);
      }
    },
    [onChange],
  );

  const canClear = value != null;

  return (
    <Select value={value ?? "all"} onValueChange={handleValueChange}>
      <div className="relative w-[180px]">
        <SelectTrigger className={canClear ? "h-8 w-full pr-12" : "h-8 w-full"}>
          <SelectValue placeholder={t("placeholder")} />
        </SelectTrigger>
        {canClear && (
          <div className="absolute inset-y-0 right-8 flex items-center">
            <IconButton
              icon="XIcon"
              variant="mutedGhost"
              size="ExtraSmall"
              type="button"
              aria-label="Clear selection"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onChange(undefined);
              }}
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            />
          </div>
        )}
      </div>
      <SelectContent>
        {statusOptions.map((status) => (
          <SelectItem key={status} value={status}>
            {t(status)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
